import { addDays } from 'date-fns/addDays';
import { useState } from 'react';
import { usePauseBillGroupDunning } from '../../../../api/dunningService';
import {
  MBox,
  MButton,
  MCenterModal,
  MFormControl,
  MFormErrorMessage,
  MFormLabel,
  MStack,
  MText,
} from '../../../../components/Monetize';
import { DatePicker } from '../../../../components/Monetize/DatePicker/DatePicker';

interface PauseDunningModalProps {
  isOpen: boolean;
  billGroupId: string;
  onClose: () => void;
}

export const PauseDunningModal = ({
  isOpen,
  onClose,
  billGroupId,
}: PauseDunningModalProps) => {
  const [pauseDate, setPauseDate] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const pauseDunning = usePauseBillGroupDunning({
    meta: { showErrorToast: true },
  });

  const handleClose = () => {
    setPauseDate(null);
    setShowError(false);
    onClose();
  };

  const handleConfirm = async () => {
    if (!pauseDate) {
      setShowError(true);
      return;
    }

    await pauseDunning.mutateAsync({
      billGroupId,
      pauseData: {
        dunningPauseExpirationDate: pauseDate,
      },
    });
    onClose();
  };

  const handleDateChange = (dateString: string | null) => {
    setPauseDate(dateString);
    if (dateString && showError) {
      setShowError(false);
    }
  };

  const isConfirmDisabled = !pauseDate || pauseDunning.isPending;

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={handleClose}
      modalTitle="Pause Dunning"
      size="md"
      modalBodyProps={{ pt: 0 }}
      closeOnEsc={false}
      closeOnOverlayClick={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="end" flex={1}>
          <MButton
            onClick={handleClose}
            variant="cancel"
            minW="auto"
            isDisabled={pauseDunning.isPending}
          >
            Cancel
          </MButton>
          <MButton
            variant="primary"
            onClick={handleConfirm}
            isDisabled={isConfirmDisabled}
            isLoading={pauseDunning.isPending}
            minW="auto"
          >
            Confirm
          </MButton>
        </MStack>
      )}
    >
      <MText>
        Pausing dunning will stop all automated past-due actions for this bill
        group. You can resume dunning at any time.
      </MText>
      <MBox mt={4}>
        <MFormControl required invalid={showError}>
          <MFormLabel>Pause Until Date</MFormLabel>
          <DatePicker
            clearable
            placeholder="Select a date"
            value={pauseDate}
            minDate={addDays(new Date(), 1)}
            onChange={handleDateChange}
          />
          {showError && (
            <MFormErrorMessage>Pause until date is required</MFormErrorMessage>
          )}
        </MFormControl>
      </MBox>
    </MCenterModal>
  );
};
